import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { companiesApi, contactsApi, customerProfilesApi, customerDocumentsApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { detailText } from "../utils/apiErrors";

const CONTACT_FIELDS = [
  ["first_name", "שם פרטי", 200], ["last_name", "שם משפחה", 200],
  ["role", "תפקיד / נותן שירות", 200], ["email", "דוא״ל", 300], ["phone", "טלפון", 50],
];
const INPUT = "w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100";
const BUTTON = "rounded-lg bg-brand-600 text-white px-4 py-2 text-sm disabled:opacity-40";

function Section({ title, children }) {
  return <section className="bg-white border rounded-xl p-5 space-y-4"><h2 className="text-lg font-bold text-gray-800">{title}</h2>{children}</section>;
}

function Field({ label, value, onChange, multiline = false, maxLength = 255, disabled = false }) {
  const props = { value: value ?? "", onChange: (e) => onChange(e.target.value || null), maxLength, disabled, className: INPUT, dir: "auto" };
  return <label className="block text-sm text-gray-600"><span className="block mb-1">{label}</span>
    {multiline ? <textarea {...props} rows={4} /> : <input {...props} />}
  </label>;
}

function ErrorMessage({ error, fallback }) {
  return <p role="alert" className="bg-red-50 text-red-700 rounded-lg p-3">{detailText(error?.response?.data?.detail, fallback)}</p>;
}

function useUnsavedWarning(dirty) {
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
}

export default function CustomerInfoPage() {
  const { companyId } = useParams();
  const { user } = useAuth();
  const companies = useQuery({ queryKey: ["companies"], queryFn: companiesApi.list });
  const canEdit = ["admin", "owner"].includes(user?.role);

  if (!canEdit) return <div dir="rtl"><h1 className="text-2xl font-bold mb-4">מידעון</h1><p role="alert">סביבת ה־IT זמינה למנהלים ולבעלים בלבד בהתאם להרשאות הקיימות.</p></div>;
  if (companies.isPending) return <p role="status" dir="rtl">טוען לקוחות...</p>;
  if (companies.isError) return <ErrorMessage error={companies.error} fallback="טעינת רשימת הלקוחות נכשלה" />;
  if (!companies.data?.length) return <p dir="rtl">אין לקוחות ברשימת הלקוחות.</p>;
  if (!companyId) return <Navigate to={`/customer-info/${companies.data[0].id}`} replace />;
  const company = companies.data.find((item) => item.id === companyId);
  return <CustomerInfoEditor key={companyId} companyId={companyId} company={company} companies={companies.data} />;
}

function CustomerInfoEditor({ companyId, company, companies }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [draft, setDraft] = useState({});
  const [contactDraft, setContactDraft] = useState(null);
  const [status, setStatus] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const fileInput = useRef(null);
  const documents = useQuery({ queryKey: ["customer-documents", companyId], queryFn: () => customerDocumentsApi.list(companyId), enabled: !!company });
  const profile = useQuery({ queryKey: ["customer-profile", companyId], queryFn: () => customerProfilesApi.get(companyId), enabled: !!company });
  const contacts = useQuery({ queryKey: ["contacts", "customer-info", companyId], queryFn: () => contactsApi.list({ company_id: companyId }), enabled: !!company });
  const dirty = Object.keys(draft).length > 0 || contactDraft !== null || selectedFile !== null;
  useUnsavedWarning(dirty);

  const save = useMutation({
    mutationFn: (payload) => customerProfilesApi.upsert(companyId, payload),
    onSuccess: (saved) => {
      qc.setQueryData(["customer-profile", companyId], saved);
      qc.invalidateQueries({ queryKey: ["customer-profiles-all"] });
      setDraft({}); setStatus("המידעון נשמר בהצלחה");
    },
  });
  const saveContact = useMutation({
    mutationFn: ({ id, ...data }) => id ? contactsApi.update(id, data) : contactsApi.create({ ...data, company_id: companyId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      setContactDraft(null); setStatus("איש הקשר נשמר בהצלחה");
    },
  });
  const upload = useMutation({
    mutationFn: (file) => customerDocumentsApi.upload(companyId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customer-documents", companyId] });
      setSelectedFile(null);
      if (fileInput.current) fileInput.current.value = "";
      setStatus("הקובץ הועלה בהצלחה");
    },
  });
  const download = useMutation({ mutationFn: (document) => customerDocumentsApi.download(companyId, document.id, document.original_filename) });
  const pending = save.isPending || saveContact.isPending || upload.isPending || download.isPending;
  const f = { ...profile.data, ...draft };
  const info = f.customer_info || {};
  const set = (key, value) => { setDraft((previous) => ({ ...previous, [key]: value })); setStatus(""); };
  const setInfo = (key, value) => set("customer_info", { ...info, [key]: value });
  const chooseCompany = (event) => {
    if (pending) return;
    if (dirty && !window.confirm("יש שינויים שלא נשמרו. לעבור ללקוח אחר ולוותר על השינויים?")) return;
    navigate(`/customer-info/${event.target.value}`);
  };
  const startContact = (contact = {}) => {
    saveContact.reset();
    setContactDraft(Object.fromEntries(["id", "first_name", "last_name", "email", "phone", "role", "notes", "is_primary"].map((key) => [key, contact[key] ?? (key === "is_primary" ? false : "")])));
  };

  return <div className="max-w-5xl mx-auto space-y-5" dir="rtl" lang="he">
    <header className="bg-white border rounded-xl p-5 space-y-3">
      <h1 className="text-2xl font-bold text-gray-800">מידעון{company ? ` — ${company.name}` : ""}</h1>
      <label className="block text-sm font-medium">בחירת לקוח
        <select aria-label="בחירת לקוח" className={`${INPUT} mt-1 max-w-md`} value={company ? companyId : ""} onChange={chooseCompany} disabled={pending}>
          {!company && <option value="">בחרו לקוח קיים</option>}
          {companies.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </label>
      {company && <p className="text-sm text-gray-500">דומיין: <bdi>{company.domain}</bdi> · פרטים חסרים נשארים ריקים.</p>}
    </header>
    {!company ? <p role="alert">הלקוח אינו ברשימת הלקוחות הקיימת.</p> : profile.isPending ? <p role="status">טוען מידעון...</p> : profile.isError ?
      <ErrorMessage error={profile.error} fallback="טעינת המידעון נכשלה. לא ניתן לערוך עד שהטעינה תצליח." /> : <>
      <Section title="אנשי קשר ונותני שירות">
        {contacts.isPending ? <p role="status">טוען אנשי קשר...</p> : contacts.isError ? <ErrorMessage error={contacts.error} fallback="טעינת אנשי הקשר נכשלה" /> : <>
          {!contacts.data?.length && <p className="text-sm text-gray-500">לא הוגדרו אנשי קשר ללקוח.</p>}
          <div className="grid md:grid-cols-2 gap-3">{contacts.data?.map((contact) => <article key={contact.id} className="border rounded-lg p-3 space-y-1">
            <h3 className="font-medium">{contact.first_name} {contact.last_name}{contact.is_primary ? " · איש קשר ראשי" : ""}</h3>
            {contact.role && <p>{contact.role}</p>}
            {contact.phone && <p><bdi>{contact.phone}</bdi></p>}
            {contact.email && <p><bdi>{contact.email}</bdi></p>}
            {contact.notes && <p className="whitespace-pre-wrap text-sm">{contact.notes}</p>}
            <button disabled={pending || contactDraft !== null} className="text-brand-600 text-sm" onClick={() => startContact(contact)}>עריכת איש קשר — {contact.first_name}</button>
          </article>)}</div>
          {!contactDraft && <button className="text-brand-600 text-sm" disabled={pending} onClick={() => startContact()}>הוסף איש קשר</button>}
        </>}
        {contactDraft && <form className="border rounded-lg p-4 space-y-3" onSubmit={(event) => { event.preventDefault(); if (!pending) saveContact.mutate(contactDraft); }}>
          <fieldset disabled={pending} className="space-y-3">
            <legend className="font-medium">{contactDraft.id ? "עריכת איש קשר" : "איש קשר חדש"}</legend>
            <div className="grid md:grid-cols-2 gap-3">{CONTACT_FIELDS.map(([key, label, maxLength]) => <label key={key} className="text-sm">{label}<input dir="auto" className={INPUT} maxLength={maxLength} required={key === "first_name"} value={contactDraft[key]} onChange={(event) => setContactDraft({ ...contactDraft, [key]: event.target.value })} /></label>)}</div>
            <Field label="הערות איש קשר" multiline maxLength={10000} value={contactDraft.notes} onChange={(value) => setContactDraft({ ...contactDraft, notes: value })} />
            <label className="flex gap-2"><input type="checkbox" checked={contactDraft.is_primary} onChange={(event) => setContactDraft({ ...contactDraft, is_primary: event.target.checked })} />איש קשר ראשי</label>
            {saveContact.isError && <ErrorMessage error={saveContact.error} fallback="שמירת איש הקשר נכשלה" />}
            <div className="flex gap-3"><button className={BUTTON} type="submit">{saveContact.isPending ? "שומר..." : "שמור איש קשר"}</button><button type="button" onClick={() => setContactDraft(null)}>ביטול</button></div>
          </fieldset>
        </form>}
      </Section>
      <fieldset disabled={pending} className="space-y-5">
      <Section title="נותני שירות">
        <p className="text-xs text-gray-500">נותני השירות של הלקוח, בנפרד מאנשי הקשר.</p>
        {!(info.service_providers || []).length && <p className="text-sm text-gray-500">לא הוגדרו נותני שירות.</p>}
        {!!info.service_providers?.length && <div className="overflow-x-auto border rounded-lg">
          <table className="w-full min-w-[600px] text-sm text-right">
            <thead className="bg-gray-50 text-gray-700"><tr>
              {["מערכת", "טלפון", "אחר", ""].map((label, i) => <th key={i} scope="col" className="px-2 py-2 font-medium">{label}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-gray-200">
              {info.service_providers.map((provider, index) => <tr key={index}>
                {([["system", "מערכת", 255], ["phone", "טלפון", 100], ["other", "אחר", 1000]]).map(([key, label, limit]) => <td key={key} className="px-2 py-1.5">
                  <input className={INPUT} dir="auto" maxLength={limit} aria-label={`${label} נותן שירות ${index + 1}`} value={provider[key] || ""} onChange={(event) => setInfo("service_providers", info.service_providers.map((item, i) => i === index ? { ...item, [key]: event.target.value } : item))} />
                </td>)}
                <td className="px-2 py-1.5"><button type="button" className="text-red-600 whitespace-nowrap" aria-label={`הסר נותן שירות ${index + 1}`} onClick={() => setInfo("service_providers", info.service_providers.filter((_, i) => i !== index))}>הסר</button></td>
              </tr>)}
            </tbody>
          </table>
        </div>}
        <button type="button" className="text-brand-600 text-sm" disabled={(info.service_providers?.length || 0) >= 100} onClick={() => setInfo("service_providers", [...(info.service_providers || []), { system: "", phone: "", other: "" }])}>הוסף נותן שירות</button>
      </Section>
        <Section title="כתובת, ספק אינטרנט ורשת">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="כתובת הלקוח" value={info.address} maxLength={1000} onChange={(value) => setInfo("address", value)} />
            <Field label="ספק אינטרנט" value={info.isp_name} onChange={(value) => setInfo("isp_name", value)} />
            <Field label="טלפון תמיכת ספק האינטרנט" value={info.isp_phone} maxLength={100} onChange={(value) => setInfo("isp_phone", value)} />
            <Field label="ספק VPN" value={f.vpn_vendor} maxLength={100} onChange={(value) => set("vpn_vendor", value)} />
          </div>
          <Field label="תיעוד הרשת — כתובות IP, ציוד, חיבורים" value={info.network_notes} multiline maxLength={10000} onChange={(value) => setInfo("network_notes", value)} />
        </Section>
        <Section title="שרתים ונכסים מנוהלים">
          <p className="text-xs text-gray-500">הנכסים הקיימים בפרופיל הלקוח מוצגים כאן, כולל תחנות קצה.</p>
          {!f.assets?.length && <p className="text-sm text-gray-500">לא הוגדרו שרתים או נכסים.</p>}
          {!!f.assets?.length && <div className="overflow-x-auto border rounded-lg">
            <table className="w-full min-w-[850px] text-sm text-right">
              <thead className="bg-gray-50 text-gray-700">
                <tr>{["שם שרת / נכס", "סוג נכס", "כתובת IP / שרת", "מערכת הפעלה", "מיקום", ""].map((label, i) => <th key={i} scope="col" className="px-2 py-2 font-medium">{label}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {f.assets.map((asset, index) => <tr key={index}>
                  {["name", "type", "ip", "os", "location"].map((key) => <td key={key} className="px-2 py-1.5">
                    <input dir="auto" aria-label={`${({ name: "שם שרת / נכס", type: "סוג נכס", ip: "כתובת IP / שרת", os: "מערכת הפעלה", location: "מיקום" })[key]} ${index + 1}`} className={INPUT} maxLength={1000} value={asset[key] || ""} onChange={(event) => set("assets", f.assets.map((item, i) => i === index ? { ...item, [key]: event.target.value } : item))} />
                  </td>)}
                  <td className="px-2 py-1.5"><button type="button" className="text-red-600 whitespace-nowrap" aria-label={`הסר נכס ${index + 1}`} onClick={() => set("assets", f.assets.filter((_, i) => i !== index))}>הסר</button></td>
                </tr>)}
              </tbody>
            </table>
          </div>}
          <button type="button" className="text-brand-600 text-sm" disabled={(f.assets?.length || 0) >= 200} onClick={() => set("assets", [...(f.assets || []), { name: "", type: "", ip: "", os: "", location: "" }])}>הוסף שרת / נכס</button>
        </Section>
        <Section title="אבטחה, גיבויים ומערכות">
          <div className="grid md:grid-cols-2 gap-4">{[["av_vendor", "מערכות אבטחה — ESET / SentinelOne / אחרות", 100], ["backup_vendor", "ספק גיבויים", 100], ["email_provider", "ספק דואר", 100], ["ms365_domain", "דומיין Microsoft 365", 255], ["ms365_tenant_id", "מזהה דייר Microsoft 365", 255]].map(([key, label, maxLength]) => <Field key={key} label={label} maxLength={maxLength} value={f[key]} onChange={(value) => set(key, value)} />)}</div>
          <Field label="תיעוד מערכות אבטחה וגיבויים" multiline maxLength={10000} value={info.security_notes} onChange={(value) => setInfo("security_notes", value)} />
        </Section>
        <Section title="הוראות טיפול בתקלות">
          <Field label="תהליכי אבחון, טיפול והסלמה" multiline maxLength={10000} value={info.troubleshooting} onChange={(value) => setInfo("troubleshooting", value)} />
        </Section>
        <Section title="תיעוד IT נוסף">
          <Field label="הערות תשתית קיימות" multiline maxLength={10000} value={f.infrastructure_notes} onChange={(value) => set("infrastructure_notes", value)} />
          <Field label="מסמכים, קישורים ונהלי עבודה" multiline maxLength={10000} value={info.documentation} onChange={(value) => setInfo("documentation", value)} />
          <Link to={`/companies/${companyId}/profile`} className="text-brand-600 text-sm">פתיחת פרופיל IT המלא</Link>
        </Section>
      </fieldset>
      <Section title="קבצים ומסמכים של הלקוח">
        <p className="text-sm text-gray-500">מסמכים, תמונות וקובצי ZIP — עד 20MB לקובץ.</p>
        <form className="space-y-3" onSubmit={(event) => { event.preventDefault(); if (selectedFile && !pending) upload.mutate(selectedFile); }}>
          <label className="block text-sm">בחירת קובץ להעלאה
            <input ref={fileInput} type="file" className={`${INPUT} mt-1`} disabled={pending}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.jpg,.jpeg,.png,.gif,.webp"
              onChange={(event) => {
                const file = event.target.files?.[0] || null;
                upload.reset(); setStatus(""); setFileError("");
                if (file && file.size > 20 * 1024 * 1024) {
                  setFileError("הקובץ גדול מ־20MB. בחרו קובץ קטן יותר."); setSelectedFile(null); event.target.value = "";
                } else setSelectedFile(file);
              }} />
          </label>
          {fileError && <p role="alert" className="text-red-700">{fileError}</p>}
          {upload.isError && <ErrorMessage error={upload.error} fallback="העלאת הקובץ נכשלה. ניתן לנסות שוב." />}
          <button type="submit" className={BUTTON} disabled={!selectedFile || pending}>{upload.isPending ? "מעלה קובץ..." : "העלה קובץ"}</button>
        </form>
        {documents.isPending ? <p role="status">טוען קבצים...</p> : documents.isError ? <div>
          <ErrorMessage error={documents.error} fallback="טעינת הקבצים נכשלה" />
          <button onClick={() => documents.refetch()} className="text-brand-600 text-sm">נסה לטעון קבצים שוב</button>
        </div> : documents.data?.length ? <ul className="space-y-2">{documents.data.map((document) => <li key={document.id} className="border rounded-lg p-3 flex flex-wrap justify-between gap-3">
          <div><bdi>{document.original_filename}</bdi><p className="text-xs text-gray-500">{(document.size_bytes / 1024).toLocaleString("he-IL", { maximumFractionDigits: 1 })} KB · {new Date(document.created_at).toLocaleDateString("he-IL")}</p></div>
          <button className="text-brand-600 text-sm" disabled={pending} onClick={() => download.mutate(document)} aria-label={`הורד ${document.original_filename}`}>הורד קובץ</button>
        </li>)}</ul> : <p className="text-sm text-gray-500">לא הועלו קבצים ללקוח.</p>}
        {download.isError && <ErrorMessage error={download.error} fallback="הורדת הקובץ נכשלה. נסו שוב." />}
      </Section>
      {save.isError && <ErrorMessage error={save.error} fallback="שמירת המידעון נכשלה. השינויים נשמרו בטופס." />}
      <footer className="sticky bottom-0 bg-white border rounded-xl p-4 flex flex-wrap gap-4 items-center">
        <button className={BUTTON} disabled={!Object.keys(draft).length || pending} onClick={() => save.mutate(draft)}>{save.isPending ? "שומר..." : "שמור מידעון"}</button>
        <button disabled={!Object.keys(draft).length || pending} onClick={() => { setDraft({}); save.reset(); setStatus(""); }}>בטל שינויי מידעון</button>
        <span role="status" className="text-sm">{status || (dirty ? "יש שינויים שלא נשמרו" : "")}</span>
      </footer>
    </>}
  </div>;
}
